import { ApiError } from '@/api/client/http';

/**
 * True when the API refused the call on AUTHORITY grounds.
 *
 * A 403 is not a failure the operator can retry, so it must never be rendered as a
 * generic error with a "Try again" button. It is a refusal, and it gets a refusal's
 * message: the destination is not permitted for this role.
 */
export function isForbidden(error: Error | null | undefined): boolean {
  return error instanceof ApiError && error.status === 403;
}

/** True when the API answered 404 — the record does not exist, or is not visible. */
export function isNotFound(error: Error | null | undefined): boolean {
  return error instanceof ApiError && error.status === 404;
}

/**
 * True when the API refused a normal business operation because an outstanding
 * mandatory agreement is unsatisfied.
 *
 * This is NOT a permission error and must never be rendered as one. The refusal
 * carries `details` naming the exact `versionId` and `requirementId` the subject
 * must satisfy; the UI routes the staff member there and then retries.
 */
export function isAgreementBlocker(error: Error | null | undefined): boolean {
  return error instanceof ApiError && error.code === 'AGREEMENT_ACCEPTANCE_REQUIRED';
}

/**
 * The `versionId` and `requirementId` an `AGREEMENT_ACCEPTANCE_REQUIRED` refusal names.
 *
 * The API returns `details: [{ path: '/versionId', code: '<uuid>' }, { path: '/requirementId',
 * code: '<uuid>' }]`. The `code` field carries the VALUE, not an error code — an unusual but
 * documented shape. Returns nulls when the refusal did not name them, so the caller can say so
 * rather than invent a destination.
 */
export function readBlockerTargets(error: Error | null | undefined): {
  versionId: string | null;
  requirementId: string | null;
} {
  if (!(error instanceof ApiError)) return { versionId: null, requirementId: null };
  const valueAt = (path: string): string | null =>
    error.details.find((detail) => detail.path === path)?.code ?? null;
  return {
    versionId: valueAt('/versionId'),
    requirementId: valueAt('/requirementId'),
  };
}

/**
 * A human sentence for an agreement failure, keyed by the provider's own code.
 *
 * The console never states a reason the server did not give. An unbound code says so
 * rather than guessing.
 */
const AGREEMENT_FAILURE_MESSAGES: Readonly<Record<string, string>> = {
  AGREEMENT_ACCEPTANCE_REQUIRED:
    'A mandatory agreement must be completed before this action can proceed.',
  AGREEMENT_VERIFICATION_REQUIRED:
    'This agreement requires a fresh identity check before it can be signed.',
  AGREEMENT_VERIFICATION_FAILED:
    'The identity check is expired, was already used, or belongs to another session.',
  AGREEMENT_SIGNING_SESSION_EXPIRED:
    'The signing session has expired. Re-open the document to sign again.',
  AGREEMENT_SIGNING_SESSION_CONSUMED:
    'This signing session was already used. Re-open the document to sign again.',
  AGREEMENT_CONTENT_CHANGED:
    'The document changed while you were signing. Review the new version and sign again.',
  AGREEMENT_SIGNATURE_UPLOAD_EXPIRED:
    'The signature upload window elapsed. Request a new upload.',
  AGREEMENT_SIGNATURE_UPLOAD_CONSUMED:
    'That signature upload was already used. Request a new upload.',
  AGREEMENT_SIGNATURE_INVALID:
    'The signature image was not a valid image of the declared format.',
  INVALID_AGREEMENT_REQUIREMENT:
    'The requirement is invalid. A vendor signature agreement must state its verification policy explicitly.',
  VERSION_CONFLICT: 'This record changed since it was loaded. Reload and try again.',
  SUBJECT_NOT_ELIGIBLE:
    'This subject is suspended, closed or unknown, so it cannot act on agreements.',
};

export function describeAgreementFailure(error: Error): string {
  if (!(error instanceof ApiError)) return error.message;
  return AGREEMENT_FAILURE_MESSAGES[error.code] ?? error.message;
}

/**
 * Describe a failure that arrived as `unknown`.
 *
 * A `catch` binding is `unknown`, and the notification API takes a plain string, so the
 * narrowing has to happen somewhere. Doing it here keeps every caller from repeating the
 * same `instanceof Error` dance, and keeps the message the server gave rather than a
 * generic one.
 */
export function describeUnknownAgreementFailure(caught: unknown): string {
  return describeAgreementFailure(caught instanceof Error ? caught : new Error(String(caught)));
}
